<template>
  <div class="admin-layout" :class="{ collapsed: sidebarCollapsed }">
    <aside class="sidebar">
      <div class="brand">
        <span class="brand-icon">潮</span>
        <span v-show="!sidebarCollapsed" class="brand-text">暗潮 AcAdmin</span>
      </div>

      <el-menu
        :default-active="activeMenu"
        :collapse="sidebarCollapsed"
        background-color="#ffffff"
        text-color="#475569"
        active-text-color="#0891b2"
        router
      >
        <el-menu-item
          v-for="group in singleMenuGroups"
          :key="'item-' + group.key"
          :index="group.items[0].path"
        >
          <i :class="group.items[0].icon"></i>
          <span slot="title">{{ group.items[0].title }}</span>
        </el-menu-item>

        <el-submenu
          v-for="group in multiMenuGroups"
          :key="'sub-' + group.key"
          :index="group.key"
        >
          <template slot="title">
            <i :class="group.icon"></i>
            <span>{{ group.title }}</span>
          </template>
          <el-menu-item
            v-for="item in group.items"
            :key="item.path"
            :index="item.path"
          >
            <i :class="item.icon"></i>
            <span slot="title">{{ item.title }}</span>
          </el-menu-item>
        </el-submenu>
      </el-menu>
    </aside>

    <div class="main-wrap">
      <header class="topbar">
        <div class="topbar-left">
          <i
            class="el-icon-s-fold toggle-btn"
            :class="{ 'el-icon-s-unfold': sidebarCollapsed }"
            @click="toggleSidebar"
          ></i>
          <el-breadcrumb separator="/">
            <el-breadcrumb-item>{{ currentTitle }}</el-breadcrumb-item>
          </el-breadcrumb>
        </div>
        <div class="topbar-right">
          <span class="admin-name">{{ adminName }}</span>
          <el-dropdown trigger="click" @command="handleCommand">
            <span class="avatar">{{ adminName.slice(0, 1) }}</span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item command="password">修改密码</el-dropdown-item>
              <el-dropdown-item command="logout">退出登录</el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </div>
      </header>

      <main class="main-content">
        <router-view />
      </main>
    </div>

    <el-dialog title="修改我的密码" :visible.sync="passwordVisible" width="440px">
      <el-form ref="passwordFormRef" :model="passwordForm" :rules="passwordRules" label-width="90px">
        <el-form-item label="原密码" prop="oldPassword">
          <el-input v-model="passwordForm.oldPassword" type="password" show-password placeholder="请输入原密码" />
        </el-form-item>
        <el-form-item label="新密码" prop="newPassword">
          <el-input v-model="passwordForm.newPassword" type="password" show-password placeholder="至少6位" />
        </el-form-item>
        <el-form-item label="确认密码" prop="confirmPassword">
          <el-input v-model="passwordForm.confirmPassword" type="password" show-password placeholder="再次输入新密码" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="passwordVisible = false">取消</el-button>
        <el-button type="primary" :loading="passwordLoading" @click="submitPassword">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'
import { changeMyPassword } from '@/api/admin'
import { buildMenuGroups } from '@/config/menu'

export default {
  name: 'AdminLayout',
  data() {
    const validateConfirm = (rule, value, callback) => {
      if (value !== this.passwordForm.newPassword) {
        callback(new Error('两次输入的密码不一致'))
      } else {
        callback()
      }
    }
    return {
      passwordVisible: false,
      passwordLoading: false,
      passwordForm: {
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      passwordRules: {
        oldPassword: [{ required: true, message: '请输入原密码', trigger: 'blur' }],
        newPassword: [
          { required: true, message: '请输入新密码', trigger: 'blur' },
          { min: 6, message: '密码不能少于6位', trigger: 'blur' }
        ],
        confirmPassword: [
          { required: true, message: '请确认新密码', trigger: 'blur' },
          { validator: validateConfirm, trigger: 'blur' }
        ]
      }
    }
  },
  computed: {
    ...mapGetters(['adminName', 'isSuperAdmin']),
    sidebarCollapsed() {
      return this.$store.state.sidebarCollapsed
    },
    activeMenu() {
      return this.$route.path
    },
    currentTitle() {
      return this.$route.meta.title || '管理后台'
    },
    menuGroups() {
      return buildMenuGroups(this.isSuperAdmin)
    },
    singleMenuGroups() {
      return this.menuGroups.filter(group => group.items.length === 1)
    },
    multiMenuGroups() {
      return this.menuGroups.filter(group => group.items.length > 1)
    }
  },
  methods: {
    ...mapMutations(['TOGGLE_SIDEBAR']),
    ...mapActions(['logout']),
    toggleSidebar() {
      this.TOGGLE_SIDEBAR()
    },
    handleCommand(cmd) {
      if (cmd === 'password') {
        this.passwordForm = { oldPassword: '', newPassword: '', confirmPassword: '' }
        this.passwordVisible = true
        this.$nextTick(() => {
          if (this.$refs.passwordFormRef) this.$refs.passwordFormRef.clearValidate()
        })
        return
      }
      if (cmd === 'logout') {
        this.logout()
        this.$router.push('/login')
      }
    },
    async submitPassword() {
      const form = this.$refs.passwordFormRef
      if (!form) return
      const valid = await form.validate().catch(() => false)
      if (!valid) return

      this.passwordLoading = true
      try {
        await changeMyPassword({
          oldPassword: this.passwordForm.oldPassword,
          newPassword: this.passwordForm.newPassword
        })
        this.$message.success('密码已修改，请重新登录')
        this.passwordVisible = false
        this.logout()
        this.$router.push('/login')
      } finally {
        this.passwordLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.admin-layout {
  display: flex;
  height: 100vh;
  background: $ac-bg;
}

.sidebar {
  width: 220px;
  background: $ac-sidebar;
  border-right: 1px solid $ac-border;
  box-shadow: 1px 0 4px rgba(15, 23, 42, 0.04);
  transition: width 0.2s;
  flex-shrink: 0;
  overflow: hidden;

  .collapsed & {
    width: 64px;
  }

  .el-menu {
    border-right: none;
  }

  .el-menu-item.is-active {
    background: rgba(8, 145, 178, 0.08) !important;
  }

  .el-menu-item:hover,
  ::v-deep .el-submenu__title:hover {
    background: rgba(8, 145, 178, 0.04) !important;
  }

  ::v-deep .el-submenu .el-menu-item {
    min-width: auto;
    padding-left: 48px !important;
  }

  ::v-deep .el-submenu__title {
    height: 56px;
    line-height: 56px;
  }
}

.brand {
  height: 56px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-bottom: 1px solid $ac-border;
  gap: 10px;
}

.brand-icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, $ac-primary, $ac-accent);
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.brand-text {
  font-size: 15px;
  font-weight: 600;
  color: $ac-text;
  white-space: nowrap;
}

.main-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.topbar {
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  background: $ac-bg-light;
  border-bottom: 1px solid $ac-border;
}

.topbar-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.toggle-btn {
  font-size: 20px;
  color: $ac-text-muted;
  cursor: pointer;

  &:hover {
    color: $ac-primary;
  }
}

.topbar-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.admin-name {
  font-size: 13px;
  color: $ac-text-muted;
}

.avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(8, 145, 178, 0.12);
  color: $ac-primary;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.main-content {
  flex: 1;
  overflow: auto;
  padding: 20px;
}
</style>
